import { switchMap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { IcsApprover as ICsApprover } from "../interfaces/csApprover";
import { IRatePlanOptionDTO } from "../interfaces/ratePlanOption";
import { ICsApprovedRequest, IThemeDetails } from "../interfaces/themeDetail";
import { DSRESTClient } from "../shared/communication-objects/restclient.service";
import { ApiSuccessfulResponse } from "../shared/models/apiSuccessfulResponse";
import { AuthService } from "./auth.service";
import { LogService } from "./log.service";
import { ThemeStatusEnum, ThemeStatusDto } from "../modules/theming/theme-details/theme-approval/theme-approval.component";
import { HttpParams } from "@angular/common/http";

@Injectable()
export class ThemingService {
  private readonly apiUrl = "theming";

  constructor(
    private restClient: DSRESTClient,
    private logger: LogService,
    private authService: AuthService
  ) {}


  activateTheme(themeId: number) {
    return this.restClient.get(`${this.apiUrl}/ActivateTheme/${themeId}`);
  }

  canCreateTheme(): Observable<boolean> {
    //Authorize Link to Edit Theme
    return this.authService.passesPolicyOf(
      this.authService.securityPolicies.RequiresManageThemes
    );
  }

  canEditTheme(): Observable<boolean> {
    return this.authService.passesPolicyOf(
      this.authService.securityPolicies.RequiresManageThemes
    );
  }

  createTheme(
    themeDetails: IThemeDetails,
    imageFile: any = null
  ): Observable<any> {
    const formData = new FormData();
    formData.append("themeDetails", JSON.stringify(themeDetails));
    if (imageFile) {
      this.logger.debug("createTheme", themeDetails, imageFile);
      formData.append("imageFile", imageFile);
    }
    return this.restClient.post(`${this.apiUrl}/CreateTheme`, formData);
  }

  getTheme(themeId: number): Observable<ApiSuccessfulResponse<IThemeDetails>> {
    return this.restClient.get<IThemeDetails>(`${this.apiUrl}/${themeId}`);
  }

  getThemes(orgId: number): Observable<any> {
    return this.restClient.get(`${this.apiUrl}/GetOrganizationThemes/${orgId}`);
  }

  getRatePlans(orgId: number): Observable<any> {
    return this.restClient.get<IRatePlanOptionDTO[]>(
      `${this.apiUrl}/GetRatePlanOptions/${orgId}`
    );
  }

  getApprovalStatus(themeId: number): Observable<ThemeStatusDto> {
    return this.restClient.getDirect<ThemeStatusDto>(
      `${this.apiUrl}/get-approval-status/${themeId}`
    );
  }


  getApprovalPermissions(themeId: number): Observable<Record<ThemeStatusEnum, boolean>> {
    return this.restClient.getDirect<Record<ThemeStatusEnum, boolean>>(
      `${this.apiUrl}/get-approval-permissions/${themeId}`
    );
  }

  saveApproval(status: ThemeStatusEnum, themeId: number, input: string = null): Observable<any> {
    let params = new HttpParams();
    if (input) {
      params = new HttpParams().set('input', input);
    }
    return this.restClient.post(`${this.apiUrl}/save-approval/${themeId}/${status}?${params.toString()}`, null);
  }

  getCsApprover(
    themeId: number
  ): Observable<ApiSuccessfulResponse<ICsApprover>> {
    return this.restClient.get<ICsApprover>(
      `${this.apiUrl}/GetCsApprover/${themeId}`
    );
  }

  saveCSApproval(formRequestModel: ICsApprovedRequest): Observable<any> {
    return this.restClient.post(`${this.apiUrl}/SaveCSApproval`, formRequestModel);
  }

  saveThemeDetails(
    themeDetails: IThemeDetails,
    imageFile: any = null
  ): Observable<any> {
    const formData = new FormData();
    formData.append("themeDetails", JSON.stringify(themeDetails));
    if (imageFile) {
      this.logger.debug("saveTheme", themeDetails, imageFile);
      formData.append("imageFile", imageFile);
    }
    return this.restClient.post(`${this.apiUrl}/SaveTheme`, formData);
  }

  public initializeThemeModel(
    billableOrgId: number
  ): Observable<IThemeDetails> {
    //SBTMTAPWP-1144: Sub-Billable orgs should derive their themes from the parent billable org
    return this.restClient.getDirect<IThemeDetails>(
      `${this.apiUrl}/GetInitialCreateThemeModel/${billableOrgId}`
    );
  }

  getAvailableCustomerApprovers(
    billableOrgId: number
  ): Observable<ICsApprover[]> {
    return this.restClient.getDirect<ICsApprover[]>(
      `${this.apiUrl}/GetAvailableCustomerApprovers/${billableOrgId}`
    );
  }

  customerUrlExists(billableOrgId: number, value: string): Observable<boolean> {
    return this.restClient
      .getDirect<{ exists: boolean }>(
        `${this.apiUrl}/companyurl-already-exists/${billableOrgId}/${value}`
      )
      .pipe(
        switchMap((response) => {
          this.logger.debug(
            `companyurl-already-exists ${billableOrgId}/${value} ${response.exists}`,
            value,
            response
          );
          return of(response.exists);
        })
      );
  }
}
